import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

// ── palette for report types ─────────────────────────────────────────────────
const RT_COLORS   = ['#FF6384','#36A2EB','#FFCE56','#4BC0C0','#9966FF','#FF9F40'];
const RT_BG       = ['#fff0f3','#eff8ff','#fffdf0','#f0fdfd','#f5f0ff','#fff7ef'];
const RT_TEXT     = ['#e11d48','#1d6fa4','#92700a','#0f766e','#6d28d9','#c2410c'];
const RT_CIRCUM   = 2 * Math.PI * 15.9;

const buildRTSegments = (details) => {
  if (!details?.length) return [];
  const total = details.reduce((s, d) => s + d.count, 0);
  if (!total) return [];
  let offset = 0;
  return details.map((d, i) => {
    const pct = parseFloat(d.percentage);
    const len = (pct / 100) * RT_CIRCUM;
    const seg = {
      ...d,
      color: d.color || RT_COLORS[i % RT_COLORS.length],
      bg: RT_BG[i % RT_BG.length],
      textColor: RT_TEXT[i % RT_TEXT.length],
      dasharray: `${len.toFixed(2)} ${(RT_CIRCUM - len).toFixed(2)}`,
      dashoffset: -offset,
    };
    offset += len;
    return seg;
  });
};

// Display-name overrides for backend report_type values
const reportTypeDisplayNames = {
    dynamic:    'Personal',
    historical: 'Trends',
    advanced:   'Total',
};

const ReportTypeSplitChart = ({ onStatsChange }) => {
    const [chartData, setChartData]     = useState(null);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [startDate, setStartDate]     = useState('');
    const [endDate, setEndDate]         = useState('');
    const [filterType, setFilterType]   = useState('current');
    const [show, setShow]               = useState(false);
    const [reRenderComponent, setReRenderComponent] = useState(false);

    const getFirstDayOfCurrentMonth = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,'0')}-01`;
    };
    const getCurrentDate = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    };
    const getFirstDayOfPreviousMonth = () => {
        const now = new Date(); now.setMonth(now.getMonth() - 1);
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,'0')}-01`;
    };
    const getLastDayOfPreviousMonth = () => {
        const now = new Date(); now.setDate(0);
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    };

    useEffect(() => {
        setStartDate(getFirstDayOfCurrentMonth());
        setEndDate(getCurrentDate());
    }, []);

    useEffect(() => {
        const h = () => setReRenderComponent(p => !p);
        window.addEventListener('storage', h);
        return () => window.removeEventListener('storage', h);
    }, []);

    const formatDateForAPI = (d, isEnd) =>
        d ? (isEnd ? `${d} 23:59:59.999` : `${d} 00:00:00.000`) : '';

    const fetchReportTypeSplit = async (start, end) => {
        setLoading(true); setError(null);
        try {
            const idToken = localStorage.getItem('idTokenBilling') ||
                JSON.parse(localStorage.getItem('loginDetails'))?.idToken || '';
            if (!idToken) { setError('api_error'); setLoading(false); return; }

            // Client-scoped filter — only applied when admin has a specific client selected
            const orgPreference      = JSON.parse(localStorage.getItem('orgPreference'))      || '';
            const clientPreference   = JSON.parse(localStorage.getItem('clientPreference'))   || '';
            const clientIdPreference = JSON.parse(localStorage.getItem('clientIdPreference')) || '';
            const hasClientFilter    = orgPreference && clientPreference && clientPreference !== 'none';

            let url = `https://api.niroggyan.com/billing/smartreport/counts?query_on=report_type&query_by=range&start=${encodeURIComponent(formatDateForAPI(start, false))}&end=${encodeURIComponent(formatDateForAPI(end, true))}`;
            if (hasClientFilter) {
                url += `&org=${encodeURIComponent(orgPreference)}&center=${encodeURIComponent(clientPreference)}`;
                if (clientIdPreference) url += `&clientId=${encodeURIComponent(clientIdPreference)}`;
            }

            const res = await axios({ method: 'GET', url, headers: { 'Authorization': idToken } });

            if (res.data.isSuccess && res.data.data?.length) {
                processChartData(res.data.data);
            } else {
                setChartData(null); setError('no_data');
            }
        } catch {
            setError('api_error');
        } finally {
            setLoading(false);
        }
    };

    const processChartData = (data) => {
        const totalCount = data.reduce((s, item) => s + parseInt(item.count || 0), 0);
        const detailedData = data
            .map((item, i) => ({
                label: reportTypeDisplayNames[item.report_type?.toLowerCase()] ||
                    (item.report_type.charAt(0).toUpperCase() + item.report_type.slice(1)),
                count: parseInt(item.count || 0),
                percentage: totalCount > 0 ? ((parseInt(item.count || 0) / totalCount) * 100).toFixed(1) : '0',
                color: RT_COLORS[i % RT_COLORS.length],
                bg: RT_BG[i % RT_BG.length],
                textColor: RT_TEXT[i % RT_TEXT.length],
            }))
            .sort((a, b) => b.count - a.count);
        setChartData({ details: detailedData, total: totalCount });
    };

    useEffect(() => {
        if (startDate && endDate) fetchReportTypeSplit(startDate, endDate);
    }, [startDate, endDate, reRenderComponent]);

    // Notify parent when total or date range changes (for header chip)
    useEffect(() => {
        if (onStatsChange) {
            onStatsChange({ total: chartData ? chartData.total : null, startDate, endDate });
        }
    }, [chartData, startDate, endDate]);

    const handleCurrentMonth  = () => { setStartDate(getFirstDayOfCurrentMonth()); setEndDate(getCurrentDate()); setFilterType('current'); };
    const handlePreviousMonth = () => { setStartDate(getFirstDayOfPreviousMonth()); setEndDate(getLastDayOfPreviousMonth()); setFilterType('previous'); };
    const applyCustomDates    = () => { setShow(false); setFilterType('custom'); };

    const segments   = buildRTSegments(chartData?.details);
    const maxCount   = segments.length > 0 ? segments[0].count : 1;
    const typeCount  = segments.length;

    const periodLabels = { current: 'Current Month', previous: 'Previous Month', custom: 'Custom Month' };

    // ── skeleton ──────────────────────────────────────────────────────────────
    const SkeletonView = () => (
        <Stack direction="row" gap={4} alignItems="flex-start" mt={1}>
            <Stack gap={2} sx={{ minWidth: 200, flexShrink: 0 }}>
                <Skeleton variant="circular" width={180} height={180} />
                {[1,2,3].map(k => (
                    <Stack key={k} direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Stack direction="row" gap={1} alignItems="center">
                            <Skeleton variant="circular" width={10} height={10} />
                            <Skeleton variant="text" width={100} height={14} />
                        </Stack>
                        <Skeleton variant="text" width={32} height={14} />
                    </Stack>
                ))}
            </Stack>
            <Stack flexGrow={1} gap={1.5} sx={{ minWidth: 0 }}>
                <Stack direction="row" gap={2} pb={0.5}>
                    <Skeleton variant="text" width={24} height={14} sx={{ flexShrink: 0 }} />
                    <Skeleton variant="text" width="25%" height={14} />
                    <Skeleton variant="text" sx={{ flexGrow: 1 }} height={14} />
                    <Skeleton variant="text" width={72} height={14} sx={{ flexShrink: 0 }} />
                </Stack>
                {[1,2,3,4,5,6].map(k => (
                    <Stack key={k} direction="row" gap={2} alignItems="center">
                        <Skeleton variant="text" width={20} height={14} sx={{ flexShrink: 0 }} />
                        <Skeleton variant="text" width="28%" height={14} sx={{ flexShrink: 0 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Skeleton variant="rectangular" width="100%" height={6} sx={{ borderRadius: '3px' }} />
                        </Box>
                        <Skeleton variant="rectangular" width={80} height={22} sx={{ borderRadius: '6px', flexShrink: 0 }} />
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );

    // ── empty / error ─────────────────────────────────────────────────────────
    const EmptyView = ({ isError }) => (
        <Stack alignItems="center" justifyContent="center" gap={2} py={5}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: isError ? '#fff1f2' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    {isError
                        ? <><path d="M12 9v4M12 17h.01" stroke="#e11d48" strokeWidth="1.8" strokeLinecap="round"/><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#e11d48" strokeWidth="1.5" strokeLinejoin="round"/></>
                        : <><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></>
                    }
                </svg>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#475569', mb: 0.5 }}>
                    {isError ? 'Failed to Load Data' : 'No Data Available'}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', maxWidth: 280 }}>
                    {isError
                        ? 'There was an error fetching report type data. Please try again.'
                        : 'No reports found for the selected date range. Try switching to a different period.'}
                </Typography>
            </Box>
            {isError && (
                <Button variant="outlined" size="small" onClick={() => fetchReportTypeSplit(startDate, endDate)}
                    sx={{ textTransform: 'none', borderColor: '#e11d48', color: '#e11d48', '&:hover': { borderColor: '#be123c', background: '#fff1f2' } }}>
                    Retry
                </Button>
            )}
        </Stack>
    );

    return (
        <Box sx={{ p: '0 20px 20px' }}>
            {/* ── Custom Date Dialog ── */}
            <Dialog open={show} onClose={() => setShow(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, fontSize: '15px' }}>Select Custom Date Range</DialogTitle>
                <DialogContent>
                    <Stack gap={2} pt={1}>
                        <Box>
                            <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 0.5 }}>Start Date</Typography>
                            <input className="customDateTag" type="date" value={startDate}
                                onChange={e => setStartDate(e.target.value)} style={{ width: '100%' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 0.5 }}>End Date</Typography>
                            <input className="customDateTag" type="date" value={endDate}
                                onChange={e => setEndDate(e.target.value)} style={{ width: '100%' }} />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="contained" size="small" onClick={applyCustomDates}
                        sx={{ textTransform: 'none', background: '#3567D6', '&:hover': { background: '#2555c0' } }}>Apply</Button>
                    <Button variant="outlined" size="small" onClick={() => setShow(false)}
                        sx={{ textTransform: 'none' }}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* ── Header row: note + period pill-buttons ── */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                {/* Note on the left */}
                <Stack direction="row" alignItems="center" gap={0.6}>

                </Stack>
                {/* Buttons on the right */}
                <Stack direction="row" gap={0.75} sx={{ background: '#F0F4FF', borderRadius: '10px', p: '4px' }}>
                    {['current','previous','custom'].map(key => (
                        <Box key={key}
                            onClick={() => key === 'custom' ? setShow(true) : key === 'current' ? handleCurrentMonth() : handlePreviousMonth()}
                            sx={{ px: 1.5, py: 0.6, borderRadius: '7px', cursor: 'pointer', background: filterType === key ? '#3567D6' : 'transparent', transition: 'background 0.18s ease' }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: filterType === key ? '#fff' : '#8897C4', whiteSpace: 'nowrap', transition: 'color 0.18s ease' }}>
                                {periodLabels[key]}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Stack>

            {/* ── Body ── */}
            {loading ? <SkeletonView /> : error ? <EmptyView isError={error === 'api_error'} /> : segments.length === 0 ? <EmptyView isError={false} /> : (
                <Stack direction="row" gap={4} flexWrap={{ xs: 'wrap', md: 'nowrap' }} alignItems="flex-start">
                    {/* ── Left: Donut + Legend ── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 200 }}>
                        <Box sx={{ position: 'relative', width: 180, height: 180 }}>
                            <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                                {segments.map((seg, i) => (
                                    <circle key={i} cx="18" cy="18" r="15.9" fill="none"
                                        stroke={seg.color} strokeWidth="3.8"
                                        strokeDasharray={seg.dasharray} strokeDashoffset={seg.dashoffset} />
                                ))}
                            </svg>
                            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '28px', lineHeight: 1, color: '#1e293b' }}>{typeCount}</Typography>
                                <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: '2px' }}>Types</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {segments.map((seg, i) => (
                                <Stack key={i} direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                                    <Stack direction="row" alignItems="center" gap={0.75}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: '12px', color: '#374151', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {seg.label}
                                        </Typography>
                                    </Stack>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#374151', flexShrink: 0 }}>{seg.percentage}%</Typography>
                                </Stack>
                            ))}
                        </Box>
                    </Box>

                    {/* ── Right: Table ── */}
                    <TableContainer sx={{ flexGrow: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['#','Report Type','Share','Count'].map(h => (
                                        <TableCell key={h} sx={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0', pb: 1.5, whiteSpace: 'nowrap', ...(h === 'Share' ? { minWidth: 140 } : {}) }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {segments.map((seg, i) => (
                                    <TableRow key={i} sx={{ '&:last-child td': { border: 0 }, '&:hover': { background: '#f8fafc' } }}>
                                        <TableCell sx={{ color: '#94a3b8', fontSize: '12px', width: 28, py: 1.5 }}>{i + 1}</TableCell>
                                        <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#1e293b', py: 1.5 }}>{seg.label}</TableCell>
                                        <TableCell sx={{ minWidth: 140, py: 1.5 }}>
                                            <Box sx={{ height: 6, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden' }}>
                                                <Box sx={{ height: '100%', width: `${Math.round((seg.count / maxCount) * 100)}%`, background: seg.color, borderRadius: 3 }} />
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5 }}>
                                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: seg.bg, color: seg.textColor, fontWeight: 600, fontSize: '12px', borderRadius: '6px', px: 1, py: 0.4, whiteSpace: 'nowrap' }}>
                                                {seg.count.toLocaleString('en-IN')}
                                                <Box component="span" sx={{ opacity: 0.65, fontWeight: 400 }}>&nbsp;{seg.percentage}%</Box>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            )}
        </Box>
    );
};

export default ReportTypeSplitChart;

